import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { fmt } from '../lib/helpers';
import Badge from '../components/Badge';

/**
 * Layer 2 — Programme overview. Hero (stage pills + progress), metric tiles,
 * project list, and a workstream summary. Data comes from the ProgrammeShell
 * outlet context plus a couple of light programme-scoped queries.
 */
export default function ProgrammeHome() {
  const { programme, projects, stages, currentStageOrder } = useOutletContext();
  const navigate = useNavigate();
  const [workstreams, setWorkstreams] = useState([]);
  const [openRaid, setOpenRaid] = useState(null);

  useEffect(() => {
    if (!programme?.id) return;
    let cancelled = false;
    (async () => {
      const [{ data: ws }, { count }] = await Promise.all([
        sb.from('workstreams').select('id, name, programme_id, project_id').eq('programme_id', programme.id),
        sb.from('raid_items').select('id', { count: 'exact', head: true })
          .eq('programme_id', programme.id).neq('status', 'closed'),
      ]);
      if (cancelled) return;
      setWorkstreams(ws || []);
      setOpenRaid(count ?? 0);
    })();
    return () => { cancelled = true; };
  }, [programme?.id]);

  const totalStages = stages.length || 1;
  const completedStages = currentStageOrder >= 0 ? currentStageOrder : 0;
  const progressPct = Math.round((completedStages / totalStages) * 100);
  const statusName = programme.programme_statuses?.name || 'Draft';

  return (
    <div className="content">
      {/* Hero */}
      <div className="prog-hero">
        <div className="prog-hero-top">
          <div>
            <div className="prog-kicker">Programme</div>
            <div className="prog-title">{programme.name}</div>
            <div className="prog-subtitle">
              {programme.organisations?.name}
              {` · ${projects.length} project${projects.length !== 1 ? 's' : ''}`}
              {programme.target_go_live ? ` · Go-live: ${fmt(programme.target_go_live)}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            <Badge variant="gr">{statusName}</Badge>
          </div>
        </div>

        {stages.length > 0 && (
          <div className="stage-pills">
            {stages.map((s) => {
              const cls = s.order_index < currentStageOrder ? 'done'
                : s.order_index === currentStageOrder ? 'current' : '';
              return <span key={s.id} className={`stage-pill ${cls}`}>{s.name}</span>;
            })}
          </div>
        )}

        <div className="hero-prog-wrap">
          <div className="hero-prog-bar"><div className="hero-prog-fill" style={{ width: `${progressPct}%` }} /></div>
          <span className="hero-prog-pct">{progressPct}% through lifecycle</span>
        </div>
      </div>

      {/* Metric tiles */}
      <div className="m4">
        <div className="met"><div className="mlbl">Projects</div><div className="mval">{projects.length}</div><div className="msub">in this programme</div></div>
        <div className="met"><div className="mlbl">Workstreams</div><div className="mval">{workstreams.length}</div><div className="msub">across projects</div></div>
        <div className="met"><div className="mlbl">Open RAID</div><div className="mval">{openRaid ?? '—'}</div><div className="msub">risks, issues & more</div></div>
        <div className="met"><div className="mlbl">Current stage</div><div className="mval" style={{ fontSize: 15 }}>{programme.programme_stages?.name || '—'}</div><div className="msub">lifecycle position</div></div>
      </div>

      {/* Projects */}
      <div className="card">
        <div className="chdr">
          <span className="ctitle">Projects</span>
        </div>
        {projects.length === 0 ? (
          <div className="empty">No projects in this programme yet.</div>
        ) : (
          projects.map((p) => (
            <div key={p.id} className="proj-row" onClick={() => navigate(`/project/${p.id}`)}>
              <div>
                <div className="pjname">{p.name}</div>
                <div style={{ fontSize: 11, color: '#888780', marginTop: 2 }}>
                  {p.target_go_live ? `Go-live: ${fmt(p.target_go_live)}` : 'No go-live date set'}
                </div>
              </div>
              <div className="pji">
                {p.programme_stages?.name && <Badge>{p.programme_stages.name}</Badge>}
                {p.programme_statuses?.name && <Badge variant="gr">{p.programme_statuses.name}</Badge>}
                <span className="parr">Open →</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
